import * as React from "react";
import "./OverviewScreen.scss";
import {FeaturePopup} from "./FeaturePopup/FeaturePopup";
import {MainMenu} from "./Menu/MainMenu";
import {FeatureDetailImages} from "./featureDetailImages/FeatureDetailImages";
import {FeatureBubbles} from "./featureBubbles/FeatureBubbles";
import {Config, MEDIA_QUERIES} from "../../../app/models/Constants";
import {TimelineMax} from "gsap/TimelineMax";
import {TweenMax} from "gsap/TweenMax";
import {Power1} from "gsap/EasePack";
import {AppConfig} from "../../../app/models/AppConfig";
import {FeatureTypeSelector} from "./featureTypeSelector/FeatureTypeSelector";

export interface OverviewScreenProps {
    data: any;
    currentSelectedLink: string;

    appConfig: AppConfig,

    isInitialDisplay: boolean;

    locale: string;
    locales: string[];
    onLocaleChangeRequest: (newLocale: string) => void;
    removeCurrentScreen: () => void;
}

enum IMG_IDS {
    CAR_HALF_TRANSPARENT_CLASSIC = "carHalfTransparentClassic",
    CAR_HALF_TRANSPARENT_ELECTRIC = "carHalfTransparentElectric",
    BACKGROUND_CAR = "backgroundCar"
}

export interface IFeature {
    $: {
        href: any;
        x: number;
        y: number;
        classicEngineOnly?: boolean;
        electricalEngineOnly?: boolean;
    };
    imgDetailInactive: {
        $: {
            src: string;
            x: number;
            y: number;
        }
    }[];
    imgDetail: {
        $: {
            src: string;
            x: number;
            y: number;
        }
    }[];
    imgPopup: {
        $: {
            src: string;
        }
    }[];
    label: string[];
    headline: string[];
    type?: IFeatureType[];
}

export interface IFeatureType {
    $: {
        href: any;
    };
    title: string[];
    img: {
        $: {
            preload: boolean;
            src: string;
        }
    }[];
}

interface IState {
    currentSelectedFeature: IFeature,
    displayTypeSelector: boolean,
    screenLocked: boolean,
    smallScreen: boolean,

    bubblesFadeInProgress: object,
    detailImagesFadeInProgress: object,
}

export class OverviewScreen extends React.Component<OverviewScreenProps, {}> {

    private _refStageHolder;
    private _refScaleContainer;
    private _refMainMenu;
    private _refImgCar;
    state: IState = {
        currentSelectedFeature: null,
        displayTypeSelector: false,
        screenLocked: true,
        smallScreen: false,

        bubblesFadeInProgress: {},
        detailImagesFadeInProgress: {},
    };
    private _refImgCarHalfTransparentClassic;
    private _refImgCarHalfTransparentElectric;

    stateAnimationProxy: any = {
        bubblesFadeInProgress: {},
        detailImagesFadeInProgress: {},
    };

    stateAnimationProxySynchronize = () => {
        this.setState(
            this.stateAnimationProxy
        );
    };


    componentDidMount() {
        this.resizeHandler();
        addEventListener("resize", this.resizeHandler);

        this.initialAnimation();
    }

    resizeHandler = (evt: any = null) => {
        const isSmallScreen: boolean = window.innerWidth <= MEDIA_QUERIES.TABLE_LS_MAX_WIDTH;
        const container: Element = document.getElementsByClassName("MesseApp")[0];
        const PARENT_WIDTH: number = container.clientWidth;
        const SCALE: number = (PARENT_WIDTH / Config.APP_WIDTH);
        const X: number = -Config.APP_WIDTH * .5;
        const Y: number = -Config.APP_HEIGHT * .5;

        TweenMax.set(this._refStageHolder, {
            width: (Config.APP_WIDTH * SCALE) + "px",
            height: (Config.APP_HEIGHT * SCALE * ((isSmallScreen) ? 1.5 : 1)) + "px"
        })

        let scaleMultiplicator: number = 1;
        if (isSmallScreen) {
            if (window.innerWidth <= MEDIA_QUERIES.MOBILE_MAX_WIDTH) {
                scaleMultiplicator = 1.035;
            } else if (window.innerWidth <= MEDIA_QUERIES.TABLE_PT_MAX_WIDTH) {
                scaleMultiplicator = 1.06;
            } else if (window.innerWidth <= MEDIA_QUERIES.TABLE_LS_MAX_WIDTH) {
                scaleMultiplicator = 1.085;
            }
        }

        TweenMax.set(this._refScaleContainer, {
            x: X,
            y: Y /* * (1.5-scaleMultiplicator*.5)*/,
            width: Config.APP_WIDTH,
            height: Config.APP_HEIGHT,
            scale: SCALE * ((isSmallScreen) ? 1.5 : 1)
        });

        if (isSmallScreen != this.state.smallScreen) {
            this.setState({
                smallScreen: isSmallScreen,
            })
        }
    };

    lockScreen = (): void => {
        this.setState({
            screenLocked: true,
        })
    };

    unlockScreen = (): void => {
        this.setState({
            screenLocked: false,
        })
    };

    openLinkOrTypeSelector = (feature: IFeature): void => {
        if (!feature.type || feature.type.length === 0) {
            window.open(this.props.appConfig.linksBaseURL + feature.$.href, "_self");
        } else {
            this.setState({
                currentSelectedFeature: feature,
                displayTypeSelector: true,
            })
        }

    };

    /**
     * Animation of screen on first enter after application start
     */
    initialAnimation = (): void => {
        const tl: TimelineMax = new TimelineMax();
        const features: IFeature[] = this.props.data.features[0].feature;

        // hide electric car on start
        tl.to(this._refImgCarHalfTransparentElectric, 0, {
            alpha: 0
        });

        tl.fromTo(this._refStageHolder, .2, {
            alpha: 0
        }, {
            alpha: 1,
            delay: .5
        });

        tl.fromTo(this._refMainMenu, .5, {
            alpha: 0
        }, {
            y: "+0px", alpha: 1, ease: Power1.easeInOut, delay: -.2
        }, "-=.2");

        tl.to(this._refImgCar, 1.5, {
            alpha: 0, ease: Power1.easeOut, delay: -.7
        });

        let del: number = 0;
        let i: number = 0;
        let m: number = 1;

        features.map((feature: IFeature, index: number) => {
            this.stateAnimationProxy.bubblesFadeInProgress[index] = {
                progress: 0
            };
            tl.to(this.stateAnimationProxy.bubblesFadeInProgress[index], .5 * m, {
                progress: 1,
                delay: (i == 0) ? 0.7 : del * m,
                onComplete: this.stateAnimationProxySynchronize,
                onUpdate: this.stateAnimationProxySynchronize
            });
            this.stateAnimationProxy.detailImagesFadeInProgress[index] = {
                progress: 0
            };
            tl.to(this.stateAnimationProxy.detailImagesFadeInProgress[index], .95 * m, {
                progress: 1,
                delay: (del - .2) * m,
                onComplete: this.stateAnimationProxySynchronize,
                onUpdate: this.stateAnimationProxySynchronize
            });

            m = 1 - (i * Math.PI * .013);
            del -= .6 * m;
            i++;
        });

        tl.call(this.unlockScreen);

        tl.play();
    };

    // ++++++++++++++++++++++++++++++
    // Mouse handling
    // ++++++++++++++++++++++++++++++

    /**
     * Handler for clicks outside, will close all highlighted features
     * @param e
     */
    outsideClickHandler = (e: any): void => {
        if (this.state.currentSelectedFeature) {
            this.setState({
                currentSelectedFeature: null
            });
        }
    };

    /**
     * Global feature mouse handler
     * @param e
     * @param {string} link
     */
    mouseEventHandler = (e: any, feature: IFeature): void => {
        (e as Event).preventDefault();

        if (this.state.screenLocked || !feature) {
            return;
        }

        switch ((e as Event).type) {
            case "mouseover":
                this.addFeatureHighlight(feature);
                break;
            case "mouseout":
                this.removeFeatureHighlight(feature);
                break;
            case "touchstart":
                if (this.state.currentSelectedFeature === feature) {
                    this.openLinkOrTypeSelector(feature);
                }
                this.addFeatureHighlight(feature);
                break;
            case "touchend":
                //this.removeFeatureHighlight(featureSet);
                break;
            case "click":
                this.openLinkOrTypeSelector(feature);
                break;
            case "dblclick":
                break;
            default:
                console.log("unknown mouse event:", (e as Event).type);
        }
    };

    // ++++++++++++++++++++++++++++++
    // Feature highlighting
    // ++++++++++++++++++++++++++++++

    /**
     * Adds and animatates feature bubble, popup an image highlights
     * @param {string} link
     */
    addFeatureHighlight = (feature: IFeature): void => {
        if (this.state.screenLocked) {
            return;
        }

        this.setState({
            currentSelectedFeature: feature
        });
    };

    /**
     * Removes and animatates feature bubble, popup an image highlights
     * @param {string} link
     * @param {boolean} callSetState
     */
    removeFeatureHighlight = (feature: IFeature, callSetState: boolean = true): void => {
        if (this.state.screenLocked) {
            return;
        }

        if (this.state.currentSelectedFeature === feature && !this.state.displayTypeSelector) {

            if (callSetState) {
                this.setState({
                    currentSelectedFeature: null
                });
            }
        }
    };

    /**
     * Removes teh type selector overlay
     * @param {string} link
     * @param {boolean} callSetState
     */
    removeTypeSelectorOverlay = (feature: IFeature, callSetState: boolean = true): void => {
        if (this.state.screenLocked) {
            return;
        }

        if (this.state.currentSelectedFeature === feature && this.state.displayTypeSelector) {

            if (callSetState) {
                this.setState({
                    displayTypeSelector: false,
                    currentSelectedFeature: null
                });
            }
        }
    };

    // ++++++++++++++++++++++++++++++
    // Helper
    // ++++++++++++++++++++++++++++++

    /**
     * Helper which returns image with given ID from image objects of data
     * @param {string} id
     * @returns {string}
     */
    getImageSrcByID = (id: string): string => {
        for (let img of this.props.data.images[0].img as any[]) {
            if (img.$.id == id) {
                return this.props.appConfig.contentPath + img.$.src;
            }
        }

        return "";
    };

    /**
     * Helper, returns the IFeature from features list, which has the mathing href
     * @param {string} link
     * @returns {IFeature}
     */
    getFeatureByLink = (link: string): IFeature => {
        const features: IFeature[] = this.props.data.features[0].feature;
        let currentFeature: IFeature;
        features.forEach((feature: IFeature) => {
            if (feature.$.href === link) {
                currentFeature = feature;
            }
        });
        return currentFeature;
    }


    // ++++++++++++++++++++++++++++++
    // stage content creation
    // ++++++++++++++++++++++++++++++

    /**
     * Method creates all feature bubbles
     * @param {IFeature} feature
     * @param {number} index
     * @returns {JSX.Element}
     */
    createFeatureBubbles = (feature: IFeature, index: number): JSX.Element => {
        const active: boolean = (this.state.currentSelectedFeature === feature) ? true : false;
        const visible: boolean = (!this.state.currentSelectedFeature || active) ? true : false;

        return <FeatureBubbles
            key={'f-bb-' + index}
            feature={feature}
            index={index}
            active={active}
            visible={visible}
            fadeInProgress={(this.state.bubblesFadeInProgress[index]) ? this.state.bubblesFadeInProgress[index].progress : 0}
            mouseEventHandler={this.mouseEventHandler}/>;
    };

    /**
     * Method creates all feature detail images (highlighted components)
     * @param {IFeature} feature
     * @param {number} index
     * @returns {JSX.Element}
     */
    createFeatureDetailImages = (feature: IFeature, index: number): JSX.Element => {
        const active: boolean = (this.state.currentSelectedFeature === feature) ? true : false;
        const visible: boolean = (this.state.currentSelectedFeature === null || active);

        return <FeatureDetailImages
            key={'fd-i-' + index}
            active={active}
            visible={visible}
            feature={feature}
            contentPath={this.props.appConfig.contentPath}
            fadeInProgress={(this.state.detailImagesFadeInProgress[index]) ? this.state.detailImagesFadeInProgress[index].progress : 0}
            index={index}/>
    };

    /**
     * Method creates all feature popups
     * @param {IFeature} feature
     * @param {number} index
     * @returns {JSX.Element}
     */
    createFeatureDetailPopup = (feature: IFeature, index: number): JSX.Element => {
        const active: boolean = (this.state.currentSelectedFeature === feature && !this.state.displayTypeSelector);
        const ID: string = 'feature-popup-' + index;
        const HEIGHT: number = feature.$.y - 50;
        const X: number = feature.$.x;
        const Y: number = feature.$.y;

        return <FeaturePopup
            id={ID}
            key={ID}
            active={active}
            displayAsOverlay={this.state.smallScreen}
            mouseOverEventHandler={this.mouseEventHandler}
            className={ID}
            x={X} y={Y} height={HEIGHT}
            imageSrc={this.props.appConfig.contentPath + feature.imgPopup[0].$.src}
            headline={feature.label.toString()}
            feature={feature}
            notifyLinkPress={this.openLinkOrTypeSelector}
            notifyClosePress={this.removeFeatureHighlight}
        />;
    };

    createTypeSelectorOverlays = (feature: IFeature, index: number): JSX.Element => {
        const active: boolean = (this.state.currentSelectedFeature === feature && this.state.displayTypeSelector);
        const ID: string = 'feature-type-selector-' + index;

        return <FeatureTypeSelector
            key={ID}
            keyString={ID}
            active={active}
            contentPath={this.props.appConfig.contentPath}
            feature={feature}
            linksBaseURL={this.props.appConfig.linksBaseURL}
            featureTypes={feature.type}
            notifyLinkPress={this.openLinkOrTypeSelector}
            notifyClosePress={this.removeTypeSelectorOverlay}
        />;
    }

    render() {
        const {currentSelectedFeature} = this.state;
        const features: IFeature[] = this.props.data.features[0].feature;
        const featureDetailImages: JSX.Element[] = features.map(this.createFeatureDetailImages);
        const featureDetailPopups: JSX.Element[] = features.map(this.createFeatureDetailPopup);
        const typeSelectorOverlays: JSX.Element[] = features.map(this.createTypeSelectorOverlays);
        const bubbles: JSX.Element[] = features.map(this.createFeatureBubbles);

        if (this._refImgCarHalfTransparentElectric && this._refImgCarHalfTransparentClassic) {
            if (currentSelectedFeature && currentSelectedFeature.$.electricalEngineOnly) {
                TweenMax.to(this._refImgCarHalfTransparentClassic, .5, {alpha: 0});
                TweenMax.to(this._refImgCarHalfTransparentElectric, .5, {alpha: 1});
            } else {
                TweenMax.to(this._refImgCarHalfTransparentClassic, .5, {alpha: 1});
                TweenMax.to(this._refImgCarHalfTransparentElectric, .5, {alpha: 0});
            }
        }

        return <div className='OverviewScreen' onClickCapture={(e) => this.outsideClickHandler(e)}>

            <div
                ref={div => this._refStageHolder = div}
                className={'stageHolder'}>
                <div className={'centerContainer'}>
                    <div
                        ref={div => this._refScaleContainer = div}
                        className={'scaleContainer'}>
                        <div className={'stage'}>
                            <img
                                ref={img => this._refImgCarHalfTransparentClassic = img}
                                key={IMG_IDS.CAR_HALF_TRANSPARENT_CLASSIC}
                                src={this.getImageSrcByID(IMG_IDS.CAR_HALF_TRANSPARENT_CLASSIC)}
                                className={'overviewImage ' + IMG_IDS.CAR_HALF_TRANSPARENT_CLASSIC}/>
                            <img
                                ref={img => this._refImgCarHalfTransparentElectric = img}
                                key={IMG_IDS.CAR_HALF_TRANSPARENT_ELECTRIC}
                                src={this.getImageSrcByID(IMG_IDS.CAR_HALF_TRANSPARENT_ELECTRIC)}
                                className={'overviewImage ' + IMG_IDS.CAR_HALF_TRANSPARENT_ELECTRIC}/>

                            <img
                                ref={img => this._refImgCar = img}
                                key={IMG_IDS.BACKGROUND_CAR}
                                src={this.getImageSrcByID(IMG_IDS.BACKGROUND_CAR)}
                                className={'overviewImage ' + IMG_IDS.BACKGROUND_CAR}/>

                            <div key={"detailImages"}>
                                {featureDetailImages}
                            </div>

                            <div key={"detailPopups"}>
                                {(!this.state.smallScreen) ? featureDetailPopups : null}
                            </div>

                            <div key={"detailBubbles"}>
                                {bubbles}
                            </div>
                        </div>
                    </div>
                </div>

                <div key={"detailPopups"}>
                    {(this.state.smallScreen) ? featureDetailPopups : null}
                </div>
            </div>

            <div ref={div => this._refMainMenu = div}>
                <MainMenu
                    currentSelectedLink={(this.state.currentSelectedFeature) ? this.state.currentSelectedFeature.$.href : null}
                    features={features}
                    mouseEventHandler={this.mouseEventHandler}/>
            </div>
            {typeSelectorOverlays}
        </div>
    }
}
